// src/services/sync/staleCache.js
// Stale-while-revalidate cache manager.
// URLs are read directly from @config — no window.__NA_CONFIG__ required.

import { APICache }                              from '../storage/idb.js';
import { BREAKERS }                              from '../circuitBreaker.js';
import { fetchNWSAlerts, fetchUSGSGauges, fetch511LA } from '../alerts/index.js';
import { NWS, USGS, DOTD, REFRESH }             from '@config';

// ── Endpoint registry ─────────────────────────────────────────────
// Dependencies: invalidating a key also invalidates its dependents.
const ENDPOINTS = {
  nws_alerts: {
    url:          NWS.alertsUrl,
    fetchFn:      () => Promise.resolve([]),  // served via Firestore listener in useAlerts (pollNWSAlerts Cloud Function)
    ttlMs:        REFRESH.alerts,
    dependencies: [],
  },
  usgs_gauges: {
    url:          USGS.gaugesUrl,
    fetchFn:      () => fetchUSGSGauges({ useCache: false }),
    ttlMs:        REFRESH.alerts,
    dependencies: ['511la_incidents'],
  },
  '511la_incidents': {
    url:          'firestore://road_incidents',
    fetchFn:      () => Promise.resolve([]),  // served via Firestore listener in useIncidents
    ttlMs:        REFRESH.traffic,
    dependencies: [],
  },
};

// In-flight guard — prevents stacking concurrent revalidations for one key
const _inFlight = new Set();

// ── Public interface ──────────────────────────────────────────────
export const StaleCacheManager = {

  // Serve from IDB immediately; revalidate in background if stale.
  // Returns: { data, stale, cachedAt } — data is null if never cached.
  async get(key) {
    const ep     = ENDPOINTS[key];
    if (!ep)     return { data: null, stale: true, cachedAt: null };
    const cached = await APICache.get(ep.url);
    if (!cached || cached.stale) _revalidate(key, ep);
    return cached ?? { data: null, stale: true, cachedAt: null };
  },

  // Force-invalidate a key and its cascade dependencies.
  async invalidate(key, { cascade = true } = {}) {
    const ep = ENDPOINTS[key];
    if (!ep) return;
    await APICache.invalidate(ep.url);
    if (cascade) {
      await Promise.all((ep.dependencies ?? []).map(
        (dep) => StaleCacheManager.invalidate(dep, { cascade: false })
      ));
    }
    _revalidate(key, ep);
  },

  // Invalidate endpoints affected by a write op type.
  async invalidateForWrite(opType) {
    const affected = {
      create_sos:     ['nws_alerts'],
      create_report:  [],
      update_gas:     ['511la_incidents'],
      update_shelter: [],
      confirm_report: [],
      flag_report:    [],
    }[opType] ?? [];
    await Promise.all(affected.map((k) => StaleCacheManager.invalidate(k)));
  },

  // Revalidate every registered endpoint — call on reconnect and app foreground.
  async revalidateAll() {
    const results = await Promise.allSettled(
      Object.entries(ENDPOINTS).map(([key, ep]) => _revalidate(key, ep))
    );
    return Object.keys(ENDPOINTS).map((key, i) => ({
      key,
      ok:    results[i].status === 'fulfilled',
      error: results[i].status === 'rejected' ? results[i].reason?.message : null,
    }));
  },

  async pruneExpired() {
    // Run in worker when available — IDB cursor scan is I/O heavy
    const { SyncWorker } = await import('@workers').catch(() => ({ SyncWorker: null }));
    if (SyncWorker) return SyncWorker.pruneApiCache();
    return APICache.prune();
  },

  async staleness(key) {
    const ep     = ENDPOINTS[key];
    const cached = ep ? await APICache.get(ep.url) : null;
    return cached ? Math.floor((Date.now() - cached.cachedAt) / 1000) : null;
  },
};

async function _revalidate(key, ep) {
  if (!ep?.url || !ep?.fetchFn || _inFlight.has(key)) return;

  // Check circuit breaker before attempting fetch
  const breakerName = ep.breaker ?? key.split('_')[0];
  const breaker     = typeof BREAKERS[breakerName] !== 'undefined' ? BREAKERS[breakerName] : null;
  if (breaker && !breaker.isAvailable()) {
    // Circuit is OPEN — serve cached data, don't attempt fetch
    window.dispatchEvent(new CustomEvent('eos-api-circuit-open', {
      detail: { key, service: breakerName, status: breaker.status() }
    }));
    return;
  }

  _inFlight.add(key);
  try {
    const fresh = await ep.fetchFn();
    await APICache.set(ep.url, fresh, ep.ttlMs);
    window.dispatchEvent(new CustomEvent('na-cache-updated', {
      detail: { key, count: Array.isArray(fresh) ? fresh.length : 1 },
    }));
  } finally {
    _inFlight.delete(key);
  }
}
